import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5002';
export const API_ORIGIN = API_BASE;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 0, // Unlimited timeout (no timeout) for bulk entries
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor — attach JWT ───────────────────────
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('sims_token') || localStorage.getItem('sims_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Response interceptor — handle 401 ─────────────────────
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      const isLoginReq = err.config?.url?.includes('/api/auth/login');
      const isLoginPage = window.location.pathname === '/login';

      sessionStorage.removeItem('sims_token');
      sessionStorage.removeItem('sims_user');
      localStorage.removeItem('sims_token');
      localStorage.removeItem('sims_user');

      if (!isLoginReq && !isLoginPage) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// ── TAG (public, no auth — used by the /t/:sku QR scan page) ─
export const tagAPI = {
  get: (sku) => axios.get(`${API_BASE}/api/tag/${encodeURIComponent(sku)}`),
};

// ── AUTH ───────────────────────────────────────────────────
export const authAPI = {
  login: (data) => api.post('/api/auth/login', data),
  changePassword: (data) => api.post('/api/auth/change-password', data),
};

// ── DASHBOARD ──────────────────────────────────────────────
export const dashboardAPI = {
  get: (branchID) => api.get('/api/dashboard', { params: { branchID } }),
  getFinancialPerformance: (trendType, branchID) => api.get('/api/dashboard/financial-performance', { params: { trendType, branchID } }),
  getItemWiseStock: (branchID) => api.get('/api/dashboard/itemwise-stock', { params: { branchID } }),
  getItemwiseSales: (branchID) => api.get('/api/dashboard/itemwise-sales', { params: { branchID } }),
};

// ── CUSTOMER ───────────────────────────────────────────────
export const customerAPI = {
  getAll: (params) => api.get('/api/customer', { params }),
  getById: (id) => api.get(`/api/customer/${id}`),
  create: (data) => api.post('/api/customer', data),
  update: (id, data) => api.put(`/api/customer/${id}`, data),
  delete: (id) => api.delete(`/api/customer/${id}`),
  getLedger: (id, params) => api.get(`/api/customer/${id}/ledger`, { params }),
};

// ── WEBSITE ORDERS ─────────────────────────────────────────
export const websiteOrderAPI = {
  getAll: (params) => api.get('/api/website/orders', { params }),
  getById: (id) => api.get(`/api/website/orders/${id}`),
  setStatus: (id, body) => api.put(`/api/website/orders/${id}/status`, body),
  retryRefund: (id, body) => api.post(`/api/website/orders/${id}/refund`, body || {}),
};

// ── COUPONS ─────────────────────────────────
export const couponAPI = {
  getAll: () => api.get('/api/website/coupons'),
  create: (data) => api.post('/api/website/coupons', data),
  update: (id, data) => api.put(`/api/website/coupons/${id}`, data),
  setActive: (id, isActive) => api.patch(`/api/website/coupons/${id}/active`, { isActive }),
  redemptions: (id) => api.get(`/api/website/coupons/${id}/redemptions`),
};

// ── NEWSLETTER ────────────────────────────────
export const subscriberAPI = {
  getAll: (params) => api.get('/api/website/subscribers', { params }),
  // A plain <a href> would not carry the bearer token and the endpoint would
  // simply refuse it, so the file comes back through the client like everything else
  exportCsv: () => api.get('/api/website/subscribers/export', { responseType: 'blob' }),
};

// ── SHIPPING (Shiprocket) ──────────────────────────────────
export const shippingAPI = {
  status: () => api.get('/api/website/shipping/status'),
  createShipment: (orderId) => api.post(`/api/website/orders/${orderId}/shipment`),
  assignAwb: (orderId, body) => api.post(`/api/website/orders/${orderId}/shipment/awb`, body || {}),
  requestPickup: (orderId) => api.post(`/api/website/orders/${orderId}/shipment/pickup`),
  track: (orderId) => api.get(`/api/website/orders/${orderId}/shipment/track`),
};

// ── SUPPLIER ───────────────────────────────────────────────
export const supplierAPI = {
  getAll: (params) => api.get('/api/supplier', { params }),
  getById: (id) => api.get(`/api/supplier/${id}`),
  create: (data) => api.post('/api/supplier', data),
  update: (id, data) => api.put(`/api/supplier/${id}`, data),
  delete: (id) => api.delete(`/api/supplier/${id}`),
  getLedger: (id, params) => api.get(`/api/supplier/${id}/ledger`, { params }),
};

// ── ITEM ───────────────────────────────────────────────────
export const itemAPI = {
  getAll: (params) => api.get('/api/item', { params }),
  getSkuList: () => api.get('/api/item/sku-list'),
  getById: (id) => api.get(`/api/item/${id}`),
  getByBarcode: (barcode) => api.get(`/api/item/barcode/${barcode}`),
  create: (data) => api.post('/api/item', data),
  update: (id, data) => api.put(`/api/item/${id}`, data),
  delete: (id) => api.delete(`/api/item/${id}`),
};

// ── INVENTORY ──────────────────────────────────────────────
export const inventoryAPI = {
  getCurrentStock: (params) => api.get('/api/inventory/current-stock', { params }),
  getItemLedger: (itemID, params) => api.get(`/api/inventory/item-ledger/${itemID}`, { params }),
};

// ── PURCHASE ───────────────────────────────────────────────
export const purchaseAPI = {
  getAll:  (params)     => api.get('/api/purchase', { params }),
  getItemView: (params) => api.get('/api/purchase/item-view', { params }),
  getById: (id)         => api.get(`/api/purchase/${id}`),
  getDetail: (detailId) => api.get(`/api/purchase/detail/${detailId}`),
  getDetailImage: (detailId) => api.get(`/api/purchase/detail/${detailId}/image`),
  create:  (data)       => api.post('/api/purchase', data),
  update:  (id, data)   => api.put(`/api/purchase/${id}`, data),
  delete:  (id)         => api.delete(`/api/purchase/${id}`),
  recordPayment: (id, data) => api.post(`/api/purchase/${id}/payment`, data),
};

// ── WEBSITE PUBLISHING (MODULE 1) ─────────────────────────
export const websiteAPI = {
  toggleWebsiteFlag: (purchaseDetailId, showOnWebsite) => api.put(`/api/purchase-items/${purchaseDetailId}/website-flag`, { showOnWebsite }),
  getWebsiteDetails: (purchaseDetailId) => api.get(`/api/purchase-items/${purchaseDetailId}/website`),
  saveWebsiteDetails: (purchaseDetailId, data) => api.put(`/api/purchase-items/${purchaseDetailId}/website`, data),
  uploadImage: (purchaseDetailId, formData) => api.post(`/api/purchase-items/${purchaseDetailId}/website/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  setPrimaryImage: (purchaseDetailId, imageId) => api.put(`/api/purchase-items/${purchaseDetailId}/website/images/${imageId}/primary`),
  setHoverImage: (purchaseDetailId, imageId) => api.put(`/api/purchase-items/${purchaseDetailId}/website/images/${imageId}/hover`),
  deleteImage: (imageId) => api.delete(`/api/website/images/${imageId}`),
  getPublishedItems: (params) => api.get('/api/website/published-items', { params }),
  savePublishedOrder: (items) => api.put('/api/website/published-items/order', { items }),
  setItemHomeSection: (purchaseDetailId, homeSection) => api.put(`/api/website/published-items/${purchaseDetailId}/home-section`, { homeSection }),
  getCatalog: (params) => axios.get(`${API_BASE}/api/public/catalog`, { params }),
  getCatalogBySlug: (slug) => axios.get(`${API_BASE}/api/public/catalog/${slug}`),
};

// ── WEBSITE CMS (MODULE 2) ──────────────────────────────
export const cmsAPI = {
  getPublicSections: () => axios.get(`${API_BASE}/api/public/cms/sections`),
  getAllSections: () => api.get('/api/cms/sections'),
  getSectionByKey: (key) => api.get(`/api/cms/sections/${key}`),
  updateSection: (key, data) => api.put(`/api/cms/sections/${key}`, data),
  uploadMedia: (key, formData) => api.post(`/api/cms/sections/${key}/media`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ── SALES ──────────────────────────────────────────────────
export const salesAPI = {
  getAll:  (params)     => api.get('/api/sales', { params }),
  getItemView: (params) => api.get('/api/sales/item-view', { params }),
  getById: (id)       => api.get(`/api/sales/${id}`),
  create:  (data)     => api.post('/api/sales', data),
  update:  (id, data) => api.put(`/api/sales/${id}`, data),
  updateOrderStatus: (id, orderStatus) => api.put(`/api/sales/${id}/order-status`, { orderStatus }),
  delete:  (id)       => api.delete(`/api/sales/${id}`),
};

// ── REPORTS ────────────────────────────────────────────────
export const reportsAPI = {
  salesRegister: (params) => api.get('/api/reports/sales-register', { params }),
  purchaseRegister: (params) => api.get('/api/reports/purchase-register', { params }),
  stockValuation: (params) => api.get('/api/reports/stock-valuation', { params }),
  gstSummary: (params) => api.get('/api/reports/gst-summary', { params }),
  lowStock: (params) => api.get('/api/reports/low-stock', { params }),
  oversoldItems: (params) => api.get('/api/reports/oversold-items', { params }),
  salesMonthlyGrowth: (params) => api.get('/api/reports/sales-monthly-growth', { params }),
};

// ── MASTER ─────────────────────────────────────────────────
export const masterAPI = {
  getCategories: () => api.get('/api/master/categories'),
  getPurities: () => api.get('/api/master/purities'),
  getCollections: () => api.get('/api/master/collections'),
  createCollection: (data) => api.post('/api/master/collections', data),
  getDesigns: (params) => api.get('/api/master/designs', { params }),
  getBranches: () => api.get('/api/master/branches'),
  createBranch: (data) => api.post('/api/master/branches', data),
  updateBranch: (id, data) => api.put(`/api/master/branches/${id}`, data),
  deleteBranch: (id) => api.delete(`/api/master/branches/${id}`),
  getRoles: () => api.get('/api/master/roles'),
};

// ── USERS ──────────────────────────────────────────────────
export const usersAPI = {
  getAll: () => api.get('/api/users'),
  getById: (id) => api.get(`/api/users/${id}`),
  create: (data) => api.post('/api/users', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  toggle: (id) => api.put(`/api/users/${id}/toggle`),
};

// ── AUDIT LOGS ──────────────────────────────────────────────
export const auditAPI = {
  getAll: (params) => api.get('/api/audit', { params }),
  getById: (id) => api.get(`/api/audit/${id}`),
};

// ── DAMAGE & EXCHANGE ───────────────────────────────────────
export const damageAPI = {
  getAll: (params) => api.get('/api/damagestock', { params }),
  create: (data) => api.post('/api/damagestock', data),
  update: (id, data) => api.put(`/api/damagestock/${id}`, data),
  delete: (id) => api.delete(`/api/damagestock/${id}`),
};

// ── SKU MASTER ──────────────────────────────────────────────
export const skuAPI = {
  get: () => api.get('/api/skumaster'),
  save: (data) => api.post('/api/skumaster/save', data),
  preview: (data) => api.post('/api/skumaster/preview', data),
  generate: (data) => api.post('/api/skumaster/generate', data),
};

// ── BACKUP ──────────────────────────────────────────────────
export const backupAPI = {
  downloadLatest: () => api.get('/api/backup/download', { responseType: 'blob' }),
};

// ── SETTINGS (Bank Details / Company Settings) ───────────────
export const settingsAPI = {
  get:  ()     => api.get('/api/settings'),
  save: (data) => api.post('/api/settings', data),
};

export default api;
